import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { shiftApi } from '@/services/api';
import type { ShiftCloseReport } from '@/types';
import { browserPrintShiftReceipt } from '@/utils/shiftReceiptPrint';
import { Loader2, Printer, X } from 'lucide-react';
import { formatMoney } from '@/utils/currency';

type DetailType = 'invoice' | 'item';

interface Props {
  shiftName: string;
  title?: string;
  onClose: () => void;
}

function money(n?: number) {
  return formatMoney(n || 0);
}

export default function ShiftPrintModal({ shiftName, title = 'Z Report', onClose }: Props) {
  const [detailType, setDetailType] = useState<DetailType>('invoice');
  const [error, setError] = useState('');

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery({
    queryKey: ['shiftCloseReport', shiftName],
    queryFn: async () => {
      const res = await shiftApi.getCloseReport(shiftName, 'invoice');
      return res.data.message as ShiftCloseReport;
    },
  });

  const printMutation = useMutation({
    mutationFn: async (type: DetailType) => {
      const res = await shiftApi.getCloseReport(shiftName, type);
      return res.data.message as ShiftCloseReport;
    },
    onSuccess: (report) => {
      browserPrintShiftReceipt({ ...report, report_type: report.report_type || 'Z' });
      onClose();
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to load shift report';
      setError(msg);
    },
  });

  const handlePrint = () => {
    setError('');
    printMutation.mutate(detailType);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {summaryLoading ? (
            <div className="flex items-center gap-2 text-gray-500 py-4">
              <Loader2 className="w-5 h-5 animate-spin" /> Loading summary...
            </div>
          ) : summaryError || !summary ? (
            <p className="text-sm text-red-600">Failed to load shift summary.</p>
          ) : (
            <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
              {summary.z_number != null && (
                <div className="flex justify-between font-semibold text-primary-800">
                  <span className="text-gray-500">Z #</span>
                  <span>{summary.z_number}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Invoices</span>
                <span>{summary.invoice_count || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Total sales</span>
                <span>{money(summary.total_sales)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cash sales</span>
                <span>{money(summary.cash_sales)}</span>
              </div>
              {(summary.return_count || 0) > 0 && (
                <div className="flex justify-between text-amber-700">
                  <span className="text-gray-500">Returns ({summary.return_count})</span>
                  <span>{money(summary.total_returns)}</span>
                </div>
              )}
              {(summary.cash_returns || 0) > 0 && (
                <div className="flex justify-between text-amber-700">
                  <span className="text-gray-500">Cash returns (-)</span>
                  <span>{money(summary.cash_returns)}</span>
                </div>
              )}
              <div className="flex justify-between text-red-600">
                <span className="text-gray-500">Drawer expenses (-)</span>
                <span>{money(summary.total_drawer_expenses)}</span>
              </div>
              {(summary.paid_in || 0) > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <span className="text-gray-500">Paid in (+)</span>
                  <span>{money(summary.paid_in)}</span>
                </div>
              )}
              {(summary.paid_out || 0) > 0 && (
                <div className="flex justify-between text-orange-600">
                  <span className="text-gray-500">Paid out (-)</span>
                  <span>{money(summary.paid_out)}</span>
                </div>
              )}
              {(summary.drops || 0) > 0 && (
                <div className="flex justify-between text-red-600">
                  <span className="text-gray-500">Drops (-)</span>
                  <span>{money(summary.drops)}</span>
                </div>
              )}
              <div className="flex justify-between font-semibold text-gray-800 border-t border-gray-200 pt-2">
                <span className="text-gray-500">Expected cash</span>
                <span>{money(summary.expected_cash)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Closing cash</span>
                <span>{money(summary.closing_amount)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Cash handover</span>
                <span>{money(summary.cash_handover)}</span>
              </div>
              <div className="flex justify-between font-semibold border-t border-gray-200 pt-2">
                <span className="text-gray-500">Difference</span>
                <span
                  className={
                    (summary.difference || 0) === 0
                      ? 'text-emerald-600'
                      : (summary.difference || 0) > 0
                        ? 'text-amber-600'
                        : 'text-red-600'
                  }
                >
                  {money(summary.difference)}
                </span>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-600">Print Z report details (optional):</p>

          <div className="space-y-2">
            <label
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer text-sm ${
                detailType === 'invoice' ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="shift-detail"
                checked={detailType === 'invoice'}
                onChange={() => setDetailType('invoice')}
                className="text-primary-600"
              />
              <span className="font-medium">Invoice-wise</span>
              <span className="text-gray-500 text-xs">Invoice ID, Amount, Status</span>
            </label>

            <label
              className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer text-sm ${
                detailType === 'item' ? 'border-primary-400 bg-primary-50/50' : 'border-gray-200'
              }`}
            >
              <input
                type="radio"
                name="shift-detail"
                checked={detailType === 'item'}
                onChange={() => setDetailType('item')}
                className="text-primary-600"
              />
              <span className="font-medium">Item-wise</span>
              <span className="text-gray-500 text-xs">Item, Qty, Amount</span>
            </label>
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="shrink-0 border-t border-gray-100 bg-gray-50 p-4 flex gap-3">
          <button
            type="button"
            onClick={handlePrint}
            disabled={printMutation.isPending}
            className="btn-primary flex-1 flex items-center justify-center gap-2"
          >
            {printMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Printer className="w-4 h-4" />
            )}
            Print
          </button>
          <button type="button" onClick={onClose} className="btn-secondary">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
